import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { StatBucket } from '../../lib/api/types'
import { cn } from '../../lib/cn'

const DEFAULT_VISIBLE = 10

/** Ranked list with proportional flat bars. Shows the top 10 by default, with an expand toggle
 *  to reveal the rest (only shown when there are more than 10 rows). */
export function Breakdown({
  title,
  data,
  maxHeight,
}: {
  title: string
  data: StatBucket[]
  maxHeight?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const sorted = [...(data ?? [])].sort((a, b) => b.value - a.value)
  const max = sorted.reduce((m, d) => Math.max(m, d.value), 0) || 1
  const total = sorted.reduce((s, d) => s + d.value, 0)
  const shown = expanded ? sorted : sorted.slice(0, DEFAULT_VISIBLE)

  return (
    <div className="cv-panel">
      <div className="flex items-center justify-between border-b border-line-soft px-4 py-3">
        <span className="text-[13px] font-semibold uppercase tracking-wide text-muted">{title}</span>
        <span className="text-xs tnum text-muted">{total.toLocaleString('it-IT')}</span>
      </div>
      <div className="space-y-2.5 overflow-y-auto p-4" style={maxHeight ? { maxHeight } : undefined}>
        {sorted.length === 0 && <p className="text-xs text-muted">Nessun dato nel periodo.</p>}
        {shown.map((d) => (
          <div key={d.label}>
            <div className="mb-1 flex items-center justify-between gap-3">
              <span className="truncate text-[13px] text-lagoon">{d.label}</span>
              <span className="shrink-0 text-[13px] font-semibold tnum text-lagoon">{d.value}</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-sm bg-paper">
              <div className="h-full rounded-sm bg-verdigris" style={{ width: `${(d.value / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
      {sorted.length > DEFAULT_VISIBLE && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center justify-center gap-1 border-t border-line-soft py-2.5 text-[12.5px] font-semibold text-muted transition-colors hover:bg-paper hover:text-coal"
        >
          {expanded ? 'Mostra meno' : `Mostra tutti (${sorted.length})`}
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')} />
        </button>
      )}
    </div>
  )
}
